<template>
  <footer class="rk-footer">
    <div class="rk-container">
      <div class="rk-footer__grid">
        <!-- Company -->
        <div class="rk-footer__col rk-footer__col--brand">
          <div class="rk-footer__brand">
            <span class="rk-footer__mark"><i class="bi bi-building"></i></span>
            <div>
              <h3>Rental<em>Kenya</em></h3>
              <p>Property management, without the spreadsheets</p>
            </div>
          </div>

          <p class="rk-footer__desc">
            The all-in-one platform for property management professionals —
            tenant records, rent tracking, and automated communication,
            tailored to how your business already runs.
          </p>

          <div class="rk-footer__badges">
            <span class="rk-badge"><i class="bi bi-star-fill"></i> 4.3 Rated</span>
            <span class="rk-badge"><i class="bi bi-people-fill"></i> 500+ Landlords</span>
          </div>

          <form class="rk-newsletter" @submit.prevent="subscribe">
            <p class="rk-newsletter__label">Property management tips, occasionally. No spam.</p>
            <div class="rk-newsletter__row">
              <input
                v-model="email"
                type="email"
                required
                placeholder="you@example.com"
                class="rk-newsletter__input"
              >
              <button type="submit" class="rk-newsletter__btn" :disabled="status === 'loading'">
                <i class="bi" :class="status === 'success' ? 'bi-check2' : 'bi-arrow-right'"></i>
              </button>
            </div>
            <p v-if="status === 'success'" class="rk-newsletter__msg">Subscribed — thank you.</p>
          </form>
        </div>

        <!-- Quick links -->
        <div class="rk-footer__col">
          <h4>Quick Links</h4>
          <ul class="rk-footer__links">
            <li><router-link to="/">Home</router-link></li>
            <li><router-link to="/features">Features</router-link></li>
            <li><router-link to="/security">Why Us</router-link></li>
            <li><router-link to="/#about">About</router-link></li>
          </ul>
        </div>

        <!-- Solutions -->
        <div class="rk-footer__col">
          <h4>Solutions</h4>
          <ul class="rk-footer__links">
            <li><a href="#">Residential Properties</a></li>
            <li><a href="#">Commercial Properties</a></li>
            <li><a href="#">Student Accommodation</a></li>
            <li><a href="#">Property Developers</a></li>
            <li><a href="#">Real Estate Agencies</a></li>
          </ul>
        </div>

        <!-- Contact -->
        <div class="rk-footer__col rk-footer__col--contact">
          <h4>Contact Us</h4>

          <a href="https://maps.google.com/?q=Pacific+Crest+Building+Mombasa+Road" target="_blank" class="rk-contact">
            <i class="bi bi-geo-alt"></i>
            <div>
              <span class="rk-contact__label">Location</span>
              <span class="rk-contact__value">Pacific Crest Building, Mombasa Road, 3rd Floor, Nairobi</span>
            </div>
          </a>

          <a href="mailto:sales@rentalkenya.com" class="rk-contact">
            <i class="bi bi-envelope"></i>
            <div>
              <span class="rk-contact__label">Email</span>
              <span class="rk-contact__value">sales@rentalkenya.com</span>
            </div>
          </a>

          <a href="tel:+254725029795" class="rk-contact">
            <i class="bi bi-telephone"></i>
            <div>
              <span class="rk-contact__label">Phone</span>
              <span class="rk-contact__value">+254 725 029 795</span>
            </div>
          </a>
        </div>
      </div>

      <div class="rk-footer__bottom">
        <div class="rk-socials">
          <a href="https://facebook.com" target="_blank" aria-label="Facebook"><i class="bi bi-facebook"></i></a>
          <a href="https://linkedin.com" target="_blank" aria-label="LinkedIn"><i class="bi bi-linkedin"></i></a>
          <a href="https://twitter.com" target="_blank" aria-label="Twitter"><i class="bi bi-twitter-x"></i></a>
          <a href="https://instagram.com" target="_blank" aria-label="Instagram"><i class="bi bi-instagram"></i></a>
        </div>

        <div class="rk-legal">
          <span>&copy; {{ currentYear }} Rental Kenya. All rights reserved.</span>
          <div class="rk-legal__links">
            <router-link to="/privacy">Privacy Policy</router-link>
            <span class="rk-dot">·</span>
            <router-link to="/terms">Terms of Service</router-link>
          </div>
        </div>
      </div>
    </div>
  </footer>
</template>

<script setup>
import { ref } from 'vue'

const currentYear = new Date().getFullYear()

// UI stub only — wire this to your actual subscribe endpoint (or a service
// like Mailchimp/Sendy) before shipping. Right now it just simulates success.
const email = ref('')
const status = ref('idle') // idle | loading | success
const subscribe = () => {
  status.value = 'loading'
  setTimeout(() => {
    status.value = 'success'
    email.value = ''
  }, 700)
}
</script>

<style scoped>
.rk-footer { background: var(--ink); color: rgba(247,246,242,0.8); padding: 5rem 0 2.5rem; }
.rk-container { max-width: 1200px; margin: 0 auto; padding: 0 1.5rem; }

.rk-footer__grid { display: grid; grid-template-columns: 1.6fr 1fr 1fr 1.4fr; gap: 3.5rem; margin-bottom: 3.5rem; }

.rk-footer__brand { display: flex; align-items: center; gap: 0.85rem; margin-bottom: 1.25rem; }
.rk-footer__mark { width: 42px; height: 42px; border-radius: var(--radius-sm); background: rgba(176,141,63,0.16); color: var(--brass-light); display: flex; align-items: center; justify-content: center; font-size: 1.2rem; flex-shrink: 0; }
.rk-footer__brand h3 { font-family: var(--font-body); font-weight: 800; font-size: 1.3rem; color: var(--paper); margin: 0; }
.rk-footer__brand h3 em { font-family: var(--font-display); font-style: italic; font-weight: 400; color: var(--brass-light); }
.rk-footer__brand p { font-family: var(--font-body); font-size: 0.8rem; color: rgba(247,246,242,0.45); margin: 0.15rem 0 0; }

.rk-footer__desc { font-family: var(--font-body); font-size: 0.92rem; line-height: 1.7; color: rgba(247,246,242,0.6); margin: 0 0 1.5rem; max-width: 340px; }

.rk-footer__badges { display: flex; gap: 0.6rem; flex-wrap: wrap; }
.rk-badge { display: inline-flex; align-items: center; gap: 0.4rem; font-family: var(--font-mono); font-size: 0.72rem; background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1); padding: 0.4rem 0.8rem; border-radius: 100px; }
.rk-badge i { color: var(--brass-light); font-size: 0.65rem; }

.rk-newsletter { margin-top: 1.75rem; }
.rk-newsletter__label { font-family: var(--font-body); font-size: 0.82rem; color: rgba(247,246,242,0.5); margin: 0 0 0.75rem; }
.rk-newsletter__row { display: flex; gap: 0.5rem; }
.rk-newsletter__input {
  flex: 1; background: rgba(0,0,0,0.25);
  box-shadow: inset 3px 3px 8px rgba(0,0,0,0.4), inset -2px -2px 6px rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.08); border-radius: var(--radius-sm);
  padding: 0.75rem 1rem; color: var(--paper); font-family: var(--font-body); font-size: 0.88rem;
}
.rk-newsletter__input::placeholder { color: rgba(247,246,242,0.35); }
.rk-newsletter__input:focus { outline: none; border-color: var(--brass); }
.rk-newsletter__btn {
  background: var(--brass); color: var(--ink); border: none; border-radius: var(--radius-sm);
  width: 44px; flex-shrink: 0; cursor: pointer; font-size: 1.05rem; transition: all 0.25s var(--ease);
}
.rk-newsletter__btn:hover { background: var(--brass-light); }
.rk-newsletter__btn:disabled { opacity: 0.6; cursor: default; }
.rk-newsletter__msg { font-family: var(--font-mono); font-size: 0.75rem; color: #7FBFA8; margin: 0.6rem 0 0; }

.rk-footer__col h4 { font-family: var(--font-body); font-weight: 700; font-size: 1rem; color: var(--paper); margin: 0 0 1.35rem; position: relative; padding-bottom: 0.6rem; }
.rk-footer__col h4::after { content: ''; position: absolute; bottom: 0; left: 0; width: 32px; height: 2px; background: var(--brass); }

.rk-footer__links { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 0.85rem; }
.rk-footer__links a { font-family: var(--font-body); font-size: 0.92rem; color: rgba(247,246,242,0.6); text-decoration: none; transition: color 0.25s var(--ease); }
.rk-footer__links a:hover { color: var(--brass-light); }

.rk-contact { display: flex; gap: 0.85rem; text-decoration: none; margin-bottom: 1.35rem; }
.rk-contact i { color: var(--brass-light); font-size: 1rem; margin-top: 0.2rem; flex-shrink: 0; }
.rk-contact__label { display: block; font-family: var(--font-mono); font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.05em; color: rgba(247,246,242,0.4); margin-bottom: 0.2rem; }
.rk-contact__value { display: block; font-family: var(--font-body); font-size: 0.88rem; color: rgba(247,246,242,0.8); line-height: 1.5; }
.rk-contact:hover .rk-contact__value { color: var(--brass-light); }

.rk-footer__bottom { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 1.5rem; padding-top: 2.25rem; border-top: 1px solid rgba(255,255,255,0.08); }

.rk-socials { display: flex; gap: 0.8rem; }
.rk-socials a { width: 38px; height: 38px; border-radius: 50%; background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1); color: rgba(247,246,242,0.7); display: flex; align-items: center; justify-content: center; text-decoration: none; transition: all 0.25s var(--ease); }
.rk-socials a:hover { background: var(--brass); color: var(--ink); border-color: var(--brass); transform: translateY(-2px); }

.rk-legal { display: flex; align-items: center; gap: 1rem; font-family: var(--font-body); font-size: 0.82rem; color: rgba(247,246,242,0.45); flex-wrap: wrap; }
.rk-legal__links { display: flex; align-items: center; gap: 0.6rem; }
.rk-legal__links a { color: rgba(247,246,242,0.45); text-decoration: none; }
.rk-legal__links a:hover { color: var(--brass-light); }
.rk-dot { color: rgba(247,246,242,0.3); }

@media (max-width: 1024px) {
  .rk-footer__grid { grid-template-columns: 1fr 1fr; gap: 2.5rem; }
}
@media (max-width: 640px) {
  .rk-footer { padding: 3.5rem 0 2rem; }
  .rk-footer__grid { grid-template-columns: 1fr; gap: 2.25rem; }
  .rk-footer__bottom { flex-direction: column; align-items: flex-start; }
}
</style>